import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Unlock, Loader2, Calendar, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATE_STYLES = {
  active: { label: "Active", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  inactive: { label: "Inactive", className: "bg-slate-100 text-slate-700 border-slate-300" },
  read_only: { label: "Closed (Read-only)", className: "bg-blue-100 text-blue-800 border-blue-300" },
  archived: { label: "Auto-archived", className: "bg-amber-100 text-amber-800 border-amber-300" },
  unlocked_for_amendment: { label: "Unlocked for amendment", className: "bg-orange-100 text-orange-800 border-orange-300" },
};

/**
 * Year-End Management — used on Settings → Year-End tab. Shows every tax year the
 * user has an entitlement for, with state pills and the unlock-for-amendment flow.
 *
 * The unlock flow requires a reason (audit-logged to unlock_history). After unlock,
 * the year becomes editable; the user can re-close it from the Year-End wizard later.
 */
export default function YearEndManagement() {
  const [entitlements, setEntitlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerBypass, setOwnerBypass] = useState(false);
  const [unlockingYear, setUnlockingYear] = useState(null);
  const [unlockReason, setUnlockReason] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  // Self-grant input (owner-only)
  const [grantYearInput, setGrantYearInput] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  const doSelfGrant = async () => {
    const y = parseInt(grantYearInput, 10);
    if (!y || y < 2000 || y > 2100) {
      toast.error("Enter a valid 4-digit tax year");
      return;
    }
    if (entitlements.some(e => e.tax_year === y)) {
      toast.error(`Tax Year ${y} already exists on your account`);
      return;
    }
    setGrantBusy(true);
    try {
      await axios.post(`${API}/year-end/owner-grant?tax_year=${y}`, {}, { withCredentials: true });
      toast.success(`Tax Year ${y} activated for your account`);
      setGrantYearInput("");
      load();
      window.dispatchEvent(new CustomEvent("tax-year:list-changed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant tax year");
    } finally {
      setGrantBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tax-year-entitlements`, { withCredentials: true });
      setEntitlements(data?.entitlements || []);
      setOwnerBypass(!!data?.owner_bypass);
    } catch (err) {
      toast.error("Failed to load Year-End data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = () => load();
    window.addEventListener("year-end:closed", refresh);
    return () => window.removeEventListener("year-end:closed", refresh);
  }, [load]);

  const doUnlock = async () => {
    if (!unlockReason.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    setUnlockBusy(true);
    try {
      await axios.post(
        `${API}/year-end/unlock?tax_year=${unlockingYear}`,
        { reason: unlockReason.trim() },
        { withCredentials: true }
      );
      toast.success(`Tax Year ${unlockingYear} unlocked for amendment`);
      setUnlockingYear(null);
      setUnlockReason("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed");
    } finally {
      setUnlockBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-slate-500"><Loader2 className="inline w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="year-end-management">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-600" /> Year-End Management</h3>
            <p className="text-sm text-slate-600 mt-1">Review every tax year, close out a finished year, or unlock a closed year for an IRS amendment (Form 1040-X).</p>
          </div>
          {ownerBypass && (
            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">Owner bypass — writes never blocked</span>
          )}
        </div>

        {/* Self-grant: owner-only "Add a new Tax Year" affordance */}
        {ownerBypass && (
          <div className="mb-4 p-3 border border-purple-200 bg-purple-50/60 rounded-lg" data-testid="owner-self-grant-panel">
            <p className="text-xs font-semibold text-purple-900 uppercase tracking-wider mb-2">
              Add a Tax Year
            </p>
            <p className="text-xs text-slate-700 mb-2">
              Activate any tax year for your account — past, current, or future. As the signature owner you have lifetime access; no payment required.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min="2000"
                max="2100"
                value={grantYearInput}
                onChange={(e) => setGrantYearInput(e.target.value)}
                placeholder="e.g. 2027"
                disabled={grantBusy}
                className="w-32 px-3 py-1.5 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-purple-500"
                data-testid="self-grant-year-input"
              />
              <button
                onClick={doSelfGrant}
                disabled={grantBusy || !grantYearInput}
                className="px-3 py-1.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white rounded-md"
                data-testid="self-grant-submit"
              >
                {grantBusy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Activate"}
              </button>
            </div>
          </div>
        )}

        <div className="divide-y divide-slate-100">
          {entitlements.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">No entitlements found. Log in once to provision the current year.</div>
          ) : (
            entitlements.map((e) => {
              const meta = STATE_STYLES[e.entitlement_state] || { label: e.entitlement_state, className: "bg-slate-100 text-slate-700" };
              return (
                <div key={e.tax_year} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`entitlement-row-${e.tax_year}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-slate-900 text-lg">{e.tax_year}</span>
                    <span className={`text-xs px-2 py-1 rounded-full border ${meta.className}`}>{meta.label}</span>
                    {e.closed_at && (
                      <span className="text-xs text-slate-500">Closed {new Date(e.closed_at).toLocaleDateString()}</span>
                    )}
                    {e.unlock_history?.length > 0 && (
                      <span className="text-xs text-orange-700 flex items-center gap-1" title={`${e.unlock_history.length} amendment unlock(s)`}>
                        <History className="w-3 h-3" /> {e.unlock_history.length}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {e.entitlement_state === "active" && (
                      <a href="/app/year-end" className="text-xs px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded inline-flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Close
                      </a>
                    )}
                    {e.entitlement_state === "inactive" && (
                      <a href={`/app/year-end/activate?tax_year=${e.tax_year}`} className="text-xs px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded">
                        Activate
                      </a>
                    )}
                    {e.entitlement_state === "read_only" && (
                      <button
                        onClick={() => { setUnlockingYear(e.tax_year); setUnlockReason(""); }}
                        className="text-xs px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded inline-flex items-center gap-1"
                        data-testid={`unlock-btn-${e.tax_year}`}
                      >
                        <Unlock className="w-3 h-3" /> Unlock for amendment
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Unlock modal */}
      {unlockingYear && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="unlock-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900">Unlock Tax Year {unlockingYear}</h3>
                <p className="text-sm text-slate-600 mt-1">Unlock should only be used for IRS amendments (Form 1040-X). The reason will be logged for audit.</p>
              </div>
            </div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
            <textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              rows={3}
              placeholder="e.g., Form 1040-X amendment to correct depreciation for asset #1234"
              className="w-full border border-slate-300 rounded-lg p-2 text-sm"
              maxLength={1000}
              data-testid="unlock-reason-input"
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button onClick={() => setUnlockingYear(null)} className="text-sm px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg">Cancel</button>
              <button
                onClick={doUnlock}
                disabled={unlockBusy || !unlockReason.trim()}
                className="text-sm px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white rounded-lg inline-flex items-center gap-2"
                data-testid="confirm-unlock-btn"
              >
                {unlockBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />} Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
